import React from "react";
import { formatIDR } from "@/lib/format";

/**
 * The ONE money breakdown block shared by: order editor footer, order detail,
 * order card, invoice page and (as text) the WhatsApp summary.
 *
 * Numbers always come from the backend calculation (compute_order /
 * POST /api/orders/preview) — this component only renders them.
 */

export function discountLabel(d) {
  const name = d?.name || "Diskon";
  if (d?.type === "percent") {
    const v = Number(d.value || 0);
    return `${name} (${Number.isInteger(v) ? v : v.toFixed(1)}%)`;
  }
  return name;
}

/** Plain-text lines (for WhatsApp / clipboard) in the same order as the UI block. */
export function breakdownTextLines({ subtotal, feeLines = [], discountLines = [], total, paid, balance, bold = false, alwaysSubtotal = false, subtotalLabel = "Subtotal Produk" }) {
  const b = (s) => (bold ? `*${s}*` : s);
  const L = [];
  const hasLines = (feeLines || []).length > 0 || (discountLines || []).length > 0;
  if (hasLines || alwaysSubtotal) L.push(`${subtotalLabel}: ${formatIDR(subtotal)}`);
  (feeLines || []).forEach((f) => L.push(`+ ${f.name || "Biaya"}: ${formatIDR(f.amount)}`));
  (discountLines || []).forEach((d) => L.push(`- ${discountLabel(d)}: -${formatIDR(d.amount)}`));
  L.push(b(`Total: ${formatIDR(total)}`));
  if (paid != null && Number(paid) > 0) {
    L.push(`Sudah dibayar: ${formatIDR(paid)}`);
    L.push(b(`Sisa tagihan: ${formatIDR(balance)}`));
  }
  return L;
}

export function PricingBreakdown({
  subtotal = 0,
  feeLines = [],
  discountLines = [],
  total = 0,
  paid = null,
  balance = null,
  overpaid = 0,
  compact = false,
  className = "",
  testId = "pricing-breakdown",
  totalLabel = "Total",
  onLineClick = null,
  lineExtra = null,
}) {
  const rowCls = compact ? "text-xs" : "text-sm";
  const fees = feeLines || [];
  const discs = discountLines || [];
  return (
    <div className={`space-y-1 ${rowCls} ${className}`} data-testid={testId}>
      <div className="flex justify-between gap-3">
        <span className="text-muted-foreground">Subtotal Produk</span>
        <span className="font-mono tabular-nums" data-testid={`${testId}-subtotal`}>{formatIDR(subtotal)}</span>
      </div>
      {fees.map((f, i) => (
        <div key={f.id || `fee-${i}`} className={`flex justify-between gap-3 ${onLineClick ? "cursor-pointer hover:text-foreground" : ""}`}
          onClick={onLineClick ? () => onLineClick("fee", f) : undefined} data-testid={`${testId}-fee-line`}>
          <span className="text-muted-foreground truncate">+ {f.name || "Biaya"}{lineExtra ? lineExtra(f) : null}</span>
          <span className="font-mono tabular-nums shrink-0">{formatIDR(f.amount)}</span>
        </div>
      ))}
      {discs.map((d, i) => (
        <div key={d.id || `disc-${i}`} className={`flex justify-between gap-3 text-[hsl(var(--warning))] ${onLineClick ? "cursor-pointer" : ""}`}
          onClick={onLineClick ? () => onLineClick("discount", d) : undefined} data-testid={`${testId}-discount-line`}>
          <span className="truncate">- {discountLabel(d)}{lineExtra ? lineExtra(d) : null}</span>
          <span className="font-mono tabular-nums shrink-0">-{formatIDR(d.amount)}</span>
        </div>
      ))}
      <div className={`flex justify-between gap-3 border-t border-border pt-1 mt-1 ${compact ? "text-sm" : "text-base"}`}>
        <span className="font-semibold">{totalLabel}</span>
        <span className="font-mono font-semibold tabular-nums" data-testid={`${testId}-total`}>{formatIDR(total)}</span>
      </div>
      {paid != null && (
        <>
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Dibayar</span>
            <span className="font-mono tabular-nums" data-testid={`${testId}-paid`}>{formatIDR(paid)}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="font-medium">Sisa</span>
            <span className={`font-mono font-semibold tabular-nums ${Number(balance) > 0 ? "text-primary" : ""}`} data-testid={`${testId}-balance`}>{formatIDR(Math.max(0, Number(balance) || 0))}</span>
          </div>
          {Number(overpaid) > 0 && (
            <div className="flex justify-between gap-3 text-[hsl(var(--warning))]">
              <span>Kelebihan Bayar</span>
              <span className="font-mono tabular-nums">{formatIDR(overpaid)}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
